import { screenLocale } from './round-config'
import { api, getQueue, mutate } from './api'
import { getMe } from './me'
import type { Lead } from './types'
import { el, toast } from './ui'
import { openWriteQuote } from './write-quote'
import { quoteReplies, type QuoteReply } from './quote-replies'

export function emailExcerpt(text: string | null): string {
  return (text ?? '').replace(/^Subject:[^\n]*(?:\r?\n){1,2}/i, '').slice(0, 200)
}
export function enquiryWhen(created: string, zone: string, now = new Date()): string {
  const date = new Date(created.endsWith('Z') ? created : created.replace(' ', 'T') + 'Z')
  if (!Number.isFinite(date.getTime())) return ''
  const day = (d: Date): string => new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
  if (day(date) === day(now)) {
    const clock24 = new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: 'numeric', minute: '2-digit', hour12: false }).format(date)
    const hour = Number(new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: 'numeric', hour12: false }).format(date))
    // "9:30 this morning", "2:15 this afternoon": the part of the day says am or pm (UK times, 12 Oct 2026).
    const clock = `${hour % 12 || 12}:${clock24.slice(-2)}`
    return `${clock} ${hour < 12 ? 'this morning' : hour < 18 ? 'this afternoon' : 'this evening'}`
  }
  return new Intl.DateTimeFormat(screenLocale(), { timeZone: zone, weekday: 'short', day: 'numeric', month: 'short' }).format(date)
}
export async function loadWaiting(): Promise<Lead[]> {
  return (await loadEmailLeads()).waiting
}
// Needs you's two email lines from one read: enquiries waiting for the owner's OK, and enquiries whose customer replied
// to the quote and the owner has not marked seen (newest reply first). Only while quotes by email is on.
export async function loadEmailLeads(): Promise<{ waiting: Lead[]; replied: Lead[] }> {
  const me = getMe()
  if (me?.role !== 'owner' || !me.businessId) return { waiting: [], replied: [] }
  const { row } = await api<{ row: { quotes_by_email: boolean } }>('/api/business/quotes-email')
  if (!row.quotes_by_email) return { waiting: [], replied: [] }
  const { leads } = await api<{ leads: Lead[] }>('/api/leads')
  const lastAt = (l: Lead): string => quoteReplies(l).at(-1)?.at ?? ''
  return {
    waiting: leads.filter(l => l.waiting_for_ok === true).sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id),
    replied: leads.filter(l => (l.replies_unseen ?? 0) > 0).sort((a, b) => lastAt(b).localeCompare(lastAt(a)) || b.id - a.id),
  }
}

// A customer's replies (0055), oldest first: src/quote-replies.ts, kept light so Messages can read them too.
export { quoteReplies, type QuoteReply } from './quote-replies'
// When a reply came, in the round's own clock, the words the waiting line uses ("09:12 this morning", "Thu 8 Oct").
export function replyWhen(r: QuoteReply): string {
  return enquiryWhen(r.at, getMe()?.round?.timeZone ?? 'Europe/London')
}
// "Seen": the replies stay, Needs you stops listing them (functions/api/leads/[id]/replies-seen.ts). Only up to what this
// phone had seen from them: a reply that came after stays to answer (Sonnet's review, S-E5, 20 Oct 2026).
export function markRepliesSeen(lead: Lead, button: HTMLButtonElement, changed: () => void): void {
  button.disabled = true
  void mutate({ label: `Seen the reply from ${lead.name}`, method: 'POST', path: `/api/leads/${lead.id}/replies-seen`, body: { upto: lead.last_in_id ?? 0 }, onAck: () => changed(), onConflict: () => changed() }).then((ack) => {
    if (!ack.ok) { button.disabled = false; toast(ack.status === 0 ? 'Saved on this phone; it goes when you have signal.' : ack.error ?? "Couldn't save that. Try again.") }
  })
}
// Needs you > Replied to your quote: who replied, when, the start of what they said, Open in New jobs and Seen.
export function repliedEntries(into: HTMLElement, leads: Lead[], changed: () => void): void {
  for (const lead of leads.slice(0, 3)) {
    const last = quoteReplies(lead).at(-1)
    const open = el('button', { type: 'button', class: 'btn-primary' }, 'Open in New jobs')
    open.addEventListener('click', () => { void import('./screens/new-jobs').then(({ openNewJobsAt }) => openNewJobsAt(lead.id)) })
    const seen = el('button', { type: 'button', class: 'btn-secondary' }, 'Seen')
    seen.addEventListener('click', () => markRepliesSeen(lead, seen, changed))
    into.append(el('div', { class: 'needs-email-row needs-email-replied' }, el('strong', {}, `Reply from ${lead.name}${last ? ` · ${replyWhen(last)}` : ''}`), el('p', {}, (last?.body ?? '').slice(0, 200)), el('div', { class: 'needs-waiting-actions' }, open, seen)))
  }
  if (leads.length > 3) into.append(el('a', { href: '#/new-jobs', class: 'needs-all' }, 'See all in New jobs →'))
}
// A change to this job still on its way: a reply being saved, or a Not this one waiting for signal. A Not this one
// tapped now would go on the version before it and be refused, or be sent twice and the second refused (Liam's notes,
// 15 Oct 2026: "often not saving"). New jobs holds the same way (leadBusy in screens/new-jobs.ts).
export function leadStillSaving(id: number): boolean {
  return getQueue().some((q) => (q.path === `/api/leads/${id}` || q.path.startsWith(`/api/leads/${id}/`)) && q.status !== 'conflict' && q.status !== 'failed')
}
export function waitingEntries(into: HTMLElement, leads: Lead[], changed: () => void): void {
  for (const lead of leads.slice(0, 3)) {
    const write = el('button', { type: 'button', class: 'btn-primary' }, 'Write the reply')
    write.addEventListener('click', () => openWriteQuote(lead, changed))
    const remove = el('button', { type: 'button', class: 'btn-secondary' }, 'Not this one')
    remove.addEventListener('click', () => {
      if (leadStillSaving(lead.id)) { toast('Still saving that job. Try again in a moment.'); return }
      remove.disabled = true
      void mutate({ label: `Not this one: ${lead.name}`, method: 'POST', path: `/api/leads/${lead.id}/not-this-one`, body: { version: lead.version }, onAck: () => { toast('Taken off New jobs.'); changed() } }).then(ack => {
        if (ack.ok) return
        // Waiting for signal: the button comes back (the waiting removal can be stopped from the changes list), and a
        // second tap while it waits says "Still saving that job" above rather than sending it twice.
        remove.disabled = false
        toast(ack.status === 0 ? 'Saved on this phone to take off when you have signal.' : ack.error ?? "Couldn't take it off. Try again.")
      })
    })
    into.append(el('div', { class: 'needs-email-row' }, el('strong', {}, `Email from ${lead.name} · ${enquiryWhen(lead.created_at, getMe()?.round?.timeZone ?? 'Europe/London')}`), el('p', {}, emailExcerpt(lead.initial_lead)), el('div', { class: 'needs-waiting-actions' }, write, remove)))
  }
  if (leads.length > 3) into.append(el('a', { href: '#/new-jobs', class: 'needs-all' }, 'See all in New jobs →'))
}
